import { Injectable, OnDestroy, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { AlertSeverityStoreModelEnum } from '@domain/alerting/alerting.enums';

import { MovableCardsContainerViewModel } from '@shared/components/movable-cards-container';
import { AlertingTableSortPropertyStoreModelEnum } from '@shared/enums/alerting-table-sort-property.enum';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { mapAlertsToAlertingCardTableViewModel } from '@shared/utils/mappings/alerting-card-table-mappings';

import { EnergyCostRange } from './enums/energy-cost-range.enum';
import { MultiSiteDashboardCard } from './enums/multi-site-dashboard-card.enum';
import { VehicleSocSummaryRange } from './enums/vehicle-soc-summary-range.enum';
import { setCustomTitleForMultiSiteDashboard } from './helpers/multi-site-dashboard-custom-title';
import { multiSiteDashboardPageLifecycleManager } from './helpers/multi-site-dashboard-page-lifecycle-manager';
import {
  clearMultiSiteDashboardData,
  loadEnergyCost,
  loadOverview,
  loadSiteAlerts,
  loadStats,
  loadVehicleStateOfChargeSummary,
  reconcileSelectedTenant,
  selectTenant,
  setCardsLayout,
  setEnergyCostRange,
  setSiteAlertsTableSortProperties,
  setVehicleSocSummaryRange,
} from './store/multi-site-dashboard.actions';
import {
  selectCardsLayout,
  selectChargersOverviewMovablePanelIsLoading,
  selectChargersOverviewMovablePanelViewModel,
  selectDashboardDepotOverviewCardIsLoading,
  selectDashboardDepotOverviewCardViewModel,
  selectEnergyCostOverviewMovablePanelIsLoading,
  selectEnergyCostOverviewMovablePanelViewModel,
  selectEnergyCostRange,
  selectHasMultipleTenants,
  selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled,
  selectOverview,
  selectOverviewIsLoading,
  selectSelectedTenant,
  selectSelectedTenantName,
  selectSiteAlerts,
  selectSiteAlertsMovablePanelIsLoading,
  selectSiteAlertsSortDirection,
  selectSiteAlertsSortProperty,
  selectSiteFleetsOverviewMovablePanelIsLoading,
  selectSiteFleetsOverviewMovablePanelViewModel,
  selectTenants,
  selectTenantsAreLoading,
  selectVehicleSocSummaryRange,
  selectVehicleStateOfChargeSummaryMovablePanelIsLoading,
  selectVehicleStateOfChargeSummaryMovablePanelViewModel,
} from './store/multi-site-dashboard.selectors';

@Injectable()
export class MultiSiteDashboardService implements OnDestroy {
  readonly #store = inject(Store);
  readonly #tenantsStore = inject(TenantsStore);

  public readonly tenants = this.#store.selectSignal(selectTenants);
  public readonly tenantsAreLoading = this.#store.selectSignal(selectTenantsAreLoading);
  public readonly selectedTenant = this.#store.selectSignal(selectSelectedTenant);
  public readonly selectedTenantName = this.#store.selectSignal(selectSelectedTenantName);
  public readonly hasMultipleTenants = this.#store.selectSignal(selectHasMultipleTenants);
  public readonly overview = this.#store.selectSignal(selectOverview);
  public readonly overviewIsLoading = this.#store.selectSignal(selectOverviewIsLoading);
  public readonly depotOverviewCardVm = this.#store.selectSignal(selectDashboardDepotOverviewCardViewModel);
  public readonly depotOverviewCardIsLoading = this.#store.selectSignal(selectDashboardDepotOverviewCardIsLoading);
  public readonly cardsLayout = this.#store.selectSignal(selectCardsLayout);
  public readonly siteFleetsOverviewVm = this.#store.selectSignal(selectSiteFleetsOverviewMovablePanelViewModel);
  public readonly siteFleetsOverviewIsLoading = this.#store.selectSignal(selectSiteFleetsOverviewMovablePanelIsLoading);
  public readonly chargersOverviewVm = this.#store.selectSignal(selectChargersOverviewMovablePanelViewModel);
  public readonly chargersOverviewIsLoading = this.#store.selectSignal(selectChargersOverviewMovablePanelIsLoading);
  readonly #siteAlerts = this.#store.selectSignal(selectSiteAlerts);
  readonly #siteAlertsSortProperty = this.#store.selectSignal(selectSiteAlertsSortProperty);
  readonly #siteAlertsSortDirection = this.#store.selectSignal(selectSiteAlertsSortDirection);
  readonly #siteAlertsDashboardIsLoading = this.#store.selectSignal(selectSiteAlertsMovablePanelIsLoading);
  readonly #isVehicleStateOfChargeSummaryEnabled = this.#store.selectSignal(selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled);
  public readonly siteAlertsIsLoading = computed(() => this.#siteAlertsDashboardIsLoading() || this.#tenantsStore.isLoading());
  public readonly siteAlertsVm = computed(() =>
    mapAlertsToAlertingCardTableViewModel(
      this.#siteAlerts(),
      this.siteAlertsIsLoading(),
      { severity: AlertSeverityStoreModelEnum.Critical },
      this.#siteAlertsSortProperty(),
      this.#siteAlertsSortDirection(),
      this.#tenantsStore.sites()?.map((site) => ({ id: site.id, name: site.name }))
    )
  );
  public readonly energyCostOverviewVm = this.#store.selectSignal(selectEnergyCostOverviewMovablePanelViewModel);
  public readonly energyCostOverviewIsLoading = this.#store.selectSignal(selectEnergyCostOverviewMovablePanelIsLoading);
  public readonly energyCostRange = this.#store.selectSignal(selectEnergyCostRange);
  public readonly vehicleSocSummaryVm = this.#store.selectSignal(selectVehicleStateOfChargeSummaryMovablePanelViewModel);
  public readonly vehicleSocSummaryIsLoading = this.#store.selectSignal(selectVehicleStateOfChargeSummaryMovablePanelIsLoading);
  public readonly vehicleSocSummaryRange = this.#store.selectSignal(selectVehicleSocSummaryRange);

  // The dashboard owns the selected tenant id and reloads dependent data when it changes.
  readonly #lifecycleManager = multiSiteDashboardPageLifecycleManager(
    () => {
      this.#store.dispatch(loadOverview());
      this.#store.dispatch(loadStats());
      this.#store.dispatch(loadSiteAlerts({}));
      this.#store.dispatch(loadEnergyCost({ range: this.energyCostRange() }));
      if (this.#isVehicleStateOfChargeSummaryEnabled() === true) {
        this.#store.dispatch(loadVehicleStateOfChargeSummary({ range: this.vehicleSocSummaryRange() }));
      }
    },
    () => {
      this.#store.dispatch(clearMultiSiteDashboardData());
    }
  );

  constructor() {
    this.#tenantsStore.ensureTenantsLoaded();
    this.#store.dispatch(reconcileSelectedTenant());
    this.#lifecycleManager.init();
    setCustomTitleForMultiSiteDashboard('multi-site-dashboard.title');
  }

  public ngOnDestroy(): void {
    this.#lifecycleManager.clearData();
  }

  public selectTenant(tenantId: string): void {
    this.#store.dispatch(selectTenant({ tenantId }));
  }

  public onCardsLayoutChange(layout: MovableCardsContainerViewModel<MultiSiteDashboardCard>): void {
    this.#store.dispatch(setCardsLayout({ layout }));
  }

  public setSiteAlertsSort(sortProperty: AlertingTableSortPropertyStoreModelEnum, sortDirection: SortDirectionStoreModelEnum): void {
    this.#store.dispatch(setSiteAlertsTableSortProperties({ sortProperty, sortDirection }));
  }

  public setEnergyCostRange(range: EnergyCostRange): void {
    this.#store.dispatch(setEnergyCostRange({ range }));
    this.#store.dispatch(loadEnergyCost({ range }));
  }

  public setVehicleSocSummaryRange(range: VehicleSocSummaryRange): void {
    this.#store.dispatch(setVehicleSocSummaryRange({ range }));
    if (this.#isVehicleStateOfChargeSummaryEnabled() === true) {
      this.#store.dispatch(loadVehicleStateOfChargeSummary({ range }));
    }
  }
}
